import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Schema } from "effect";
import {
  CheckCircle,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { useState } from "react";

import { useTRPC } from "@/infrastructure/trpc/react";
import { useAppForm } from "@/shared/forms/form-context";
import { FormInput } from "@/shared/forms/form-input";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { WebLayout } from "@/web/layout/web-layout";
import { Email } from "@vivus/common";

export const Route = createFileRoute("/_web/waitlist")({
  component: WaitlistPage,
});

const WaitlistSchema = Schema.Struct({
  email: Email.pipe(
    Schema.annotate({
      message: "Valid email is required",
    })
  ),
  honeypot: Schema.UndefinedOr(Schema.String),
});

function WaitlistPage() {
  const trpc = useTRPC();
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const submitWaitlist = useMutation(
    trpc.contact.submitWaitlist.mutationOptions({
      onSuccess: () => {
        setSubmitStatus("success");
        form.reset();
      },
      onError: (error) => {
        console.error("Waitlist submission error:", error);
        setSubmitStatus("error");
      },
    })
  );

  const form = useAppForm({
    defaultValues: {
      email: "",
      honeypot: "" as string | undefined,
    },
    validators: {
      onChange: Schema.toStandardSchemaV1(WaitlistSchema),
    },
    onSubmit: async ({ value }) => {
      setSubmitStatus("idle");
      await submitWaitlist.mutateAsync(value);
    },
  });

  return (
    <WebLayout>
      <section className="py-16 md:py-24">
        <div className="container mx-auto max-w-xl px-4 md:px-6">
          <div className="mb-10 text-center">
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h1 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Get Early Access
            </h1>
            <p className="mx-auto max-w-md text-lg text-muted-foreground">
              Be the first to know when vivus launches. Get
              early access to AI-powered infrastructure
              monitoring.
            </p>
          </div>

          <Card variant="elevated">
            <CardContent variant="pt6">
              {submitStatus === "success" ? (
                <div className="py-8 text-center">
                  <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-success">
                    <CheckCircle className="h-8 w-8 text-success-foreground" />
                  </div>
                  <h2 className="mb-2 text-xl font-semibold">
                    You're on the list!
                  </h2>
                  <p className="text-muted-foreground">
                    We'll notify you when vivus is ready.
                    Thank you for your interest!
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    await form.handleSubmit();
                  }}
                  className="space-y-6"
                >
                  <FormInput
                    form={form}
                    name="email"
                    label="Email Address"
                    placeholder="you@company.com"
                    type="email"
                    required
                  />

                  {/* Honeypot field - hidden from users */}
                  <form.Field name="honeypot">
                    {(field) => (
                      <div className="hidden">
                        <input
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onChange={(e) => {
                            field.handleChange(
                              e.target.value
                            );
                          }}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </div>
                    )}
                  </form.Field>

                  {submitStatus === "error" && (
                    <div className="flex items-center gap-2 rounded-lg border border-destructive bg-destructive p-3">
                      <AlertCircle className="size-5 text-destructive" />
                      <span className="text-sm font-medium text-destructive">
                        Something went wrong. Please try
                        again.
                      </span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={
                      form.state.isSubmitting ||
                      submitWaitlist.isPending
                    }
                    className="w-full"
                    size="lg"
                  >
                    {form.state.isSubmitting ||
                    submitWaitlist.isPending ? (
                      <div className="flex items-center gap-2">
                        <div className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Joining...
                      </div>
                    ) : (
                      "Get Early Access"
                    )}
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    We respect your privacy. No spam, ever.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </WebLayout>
  );
}
